<template>
    <div class="qi-page">
        <div class="qi-page-header">
            <h4 class="mb-0 text-primary">Report Centre</h4>
            <small class="text-muted">Every financial and business report Dukakit can generate, in one place.</small>
        </div>

        <div v-if="loading" class="text-center py-5"><span class="spinner-border"></span></div>

        <template v-else>
            <div v-for="category in categories" :key="category" class="mb-4">
                <h6 class="text-uppercase text-muted category-heading">{{ category }}</h6>
                <div class="row">
                    <div class="col-12 col-md-6 col-lg-4 mb-3" v-for="report in byCategory[category]" :key="report.key">
                        <div class="report-card" :class="{ 'report-card--unavailable': !report.route }">
                            <div class="report-card__title">{{ report.title }}</div>
                            <p class="report-card__desc">{{ report.description }}</p>
                            <div class="d-flex align-items-center flex-wrap mt-2" style="gap:6px;">
                                <a v-if="report.route" :href="report.route" class="btn btn-primary btn-sm">Open report</a>
                                <template v-if="report.download_url">
                                    <button
                                        v-for="fmt in report.export"
                                        :key="fmt"
                                        class="btn btn-outline-primary btn-sm"
                                        :disabled="downloading === report.key + fmt"
                                        @click="downloadReport(report, fmt)"
                                    >
                                        <i :class="downloading === report.key + fmt ? 'bi bi-arrow-clockwise spin' : 'bi bi-download'" class="mr-1"></i>{{ fmt.toUpperCase() }}
                                    </button>
                                </template>
                                <span v-else-if="report.export && report.export.length" class="badge badge-secondary" v-for="fmt in report.export" :key="fmt">{{ fmt.toUpperCase() }}</span>
                            </div>
                            <div v-if="!report.route" class="text-muted small mt-2"><i class="bi bi-info-circle mr-1"></i>{{ report.unavailable_reason || 'Not yet available.' }}</div>
                            <div v-if="report.note" class="text-muted small mt-2"><i class="bi bi-exclamation-triangle mr-1"></i>{{ report.note }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { downloadFile } from '../../services/pdfDownload';
export default {
    name: 'ReportCentre',
    data() {
        return {
            loading: true,
            reports: [],
            downloading: null,
        };
    },
    computed: {
        categories() {
            const order = ['Overview', 'Sales', 'Receivables', 'Payables', 'Expenses', 'Purchases', 'Tax', 'Inventory', 'Customers', 'Suppliers', 'Profitability', 'Payroll', 'Audit'];
            const present = [...new Set(this.reports.map(r => r.category))];
            return order.filter(c => present.includes(c)).concat(present.filter(c => !order.includes(c)));
        },
        byCategory() {
            const map = {};
            this.reports.forEach(r => {
                if (!map[r.category]) map[r.category] = [];
                map[r.category].push(r);
            });
            return map;
        },
    },
    created() {
        this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                const { data } = await axios.get('/api/reports/finance/registry');
                this.reports = data;
            } catch (e) {
                this.$toasts.push({ type: 'error', message: 'Could not load the report catalogue.' });
            } finally {
                this.loading = false;
            }
        },
        async downloadReport(report, format) {
            const key = report.key + format;
            this.downloading = key;
            try {
                if (report.download_method === 'get') {
                    const url = format === 'excel' && report.download_url_excel ? report.download_url_excel : report.download_url;
                    const params = new URLSearchParams({
                        from: this.defaultFrom(),
                        to: this.defaultTo(),
                    });
                    await downloadFile(`${url}?${params.toString()}`, `${report.key}.${format === 'excel' ? 'xlsx' : format}`);
                    return;
                }

                // POST flow (P&L / Sales Tax / General Ledger): the backend
                // returns a direct URL for PDF (uploaded to R2), or a
                // report_id + download_url for CSV (served from local
                // storage via a separate route) — never a bare relative
                // path a browser could open directly.
                const { data } = await axios.post(report.download_url, {
                    start_date: this.defaultFrom(),
                    end_date: this.defaultTo(),
                    format,
                });
                const result = data.data || data;
                const finalUrl = result.url || result.download_url;
                if (!finalUrl) {
                    throw new Error('No download URL returned');
                }
                await downloadFile(finalUrl, result.filename || `${report.key}.${format}`);
            } catch (e) {
                this.$toasts.push({ type: 'error', message: `Could not download ${report.title}.` });
            } finally {
                this.downloading = null;
            }
        },
        defaultFrom() {
            const d = new Date();
            d.setDate(d.getDate() - 30);
            return d.toISOString().slice(0, 10);
        },
        defaultTo() {
            return new Date().toISOString().slice(0, 10);
        },
    },
};
</script>

<style scoped>
.small { margin: 0; }
.qi-page { padding: 16px; max-width: 1300px; margin: 0 auto; }
@media (max-width: 768px) { .qi-page { padding-bottom: 90px; } }
.qi-page-header { margin-bottom: 20px; }
.category-heading { font-size: 12px; letter-spacing: 1px; margin-bottom: 10px; }
.report-card {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 16px;
    height: 100%;
    background: #fff;
}
.report-card--unavailable { background: #fafafa; }
.report-card__title { font-weight: 600; margin-bottom: 4px; }
.report-card__desc { font-size: 13px; color: #6c757d; margin-bottom: 0; }
.report-card__export { margin-top: 8px; }
.spin { display: inline-block; animation: qi-spin 0.8s linear infinite; }
@keyframes qi-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
